import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js'

const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined

let userPool: CognitoUserPool | null = null

function isValidUserPoolId(id: string | undefined): id is string {
  if (!id || typeof id !== 'string') return false
  // User Pool ID format: {region}_{id}, e.g., us-east-1_ABC123XYZ
  return /^[\w-]+_[\w]+$/.test(id)
}

function isValidClientId(id: string | undefined): id is string {
  if (!id || typeof id !== 'string') return false
  return id.length > 0 && id !== 'undefined'
}

function getUserPool(): CognitoUserPool | null {
  if (!isValidUserPoolId(userPoolId) || !isValidClientId(clientId)) {
    return null
  }
  if (!userPool) {
    userPool = new CognitoUserPool({
      UserPoolId: userPoolId,
      ClientId: clientId,
    })
  }
  return userPool
}

export interface CognitoTokens {
  idToken: string
  accessToken: string
  refreshToken: string
  expiresAt: number
}

export interface CognitoAuthSuccess {
  type: 'success'
  tokens: CognitoTokens
  username: string
}

export interface CognitoNewPasswordRequired {
  type: 'newPasswordRequired'
  cognitoUser: CognitoUser
  userAttributes: Record<string, string>
  requiredAttributes: string[]
  username: string
}

export type CognitoAuthResult = CognitoAuthSuccess | CognitoNewPasswordRequired

export function isCognitoConfigured(): boolean {
  return isValidUserPoolId(userPoolId) && isValidClientId(clientId)
}

export function signIn(username: string, password: string): Promise<CognitoAuthResult> {
  return new Promise((resolve, reject) => {
    const pool = getUserPool()
    if (!pool) {
      reject(new Error('Cognito not configured'))
      return
    }

    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: pool,
    })

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    })

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session: CognitoUserSession) => {
        const idToken = session.getIdToken()
        const accessToken = session.getAccessToken()
        const refreshToken = session.getRefreshToken()

        resolve({
          type: 'success',
          tokens: {
            idToken: idToken.getJwtToken(),
            accessToken: accessToken.getJwtToken(),
            refreshToken: refreshToken.getToken(),
            expiresAt: idToken.getExpiration() * 1000,
          },
          username: username,
        })
      },
      onFailure: (err) => {
        reject(err)
      },
      newPasswordRequired: (userAttributes: Record<string, string>, requiredAttributes: string[]) => {
        // Remove attributes that shouldn't be passed back to completeNewPasswordChallenge
        delete userAttributes.email_verified
        delete userAttributes.phone_number_verified

        resolve({
          type: 'newPasswordRequired',
          cognitoUser,
          userAttributes,
          requiredAttributes,
          username,
        })
      },
    })
  })
}

export function completeNewPassword(
  cognitoUser: CognitoUser,
  newPassword: string,
  userAttributes: Record<string, string> = {}
): Promise<CognitoAuthSuccess> {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (session: CognitoUserSession) => {
        const idToken = session.getIdToken()
        const accessToken = session.getAccessToken()
        const refreshToken = session.getRefreshToken()

        resolve({
          type: 'success',
          tokens: {
            idToken: idToken.getJwtToken(),
            accessToken: accessToken.getJwtToken(),
            refreshToken: refreshToken.getToken(),
            expiresAt: idToken.getExpiration() * 1000,
          },
          username: cognitoUser.getUsername(),
        })
      },
      onFailure: (err) => {
        reject(err)
      },
    })
  })
}

export function refreshSession(
  username: string,
  refreshToken: string
): Promise<CognitoTokens> {
  return new Promise((resolve, reject) => {
    const pool = getUserPool()
    if (!pool) {
      reject(new Error('Cognito not configured'))
      return
    }

    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: pool,
    })

    const cognitoRefreshToken = new CognitoRefreshToken({
      RefreshToken: refreshToken,
    })

    cognitoUser.refreshSession(cognitoRefreshToken, (err, session) => {
      if (err) {
        reject(err)
        return
      }

      const idToken = session.getIdToken()
      const accessToken = session.getAccessToken()

      resolve({
        idToken: idToken.getJwtToken(),
        accessToken: accessToken.getJwtToken(),
        refreshToken: refreshToken,
        expiresAt: idToken.getExpiration() * 1000,
      })
    })
  })
}

export function signOut(): void {
  const pool = getUserPool()
  if (!pool) return

  const cognitoUser = pool.getCurrentUser()
  if (cognitoUser) {
    cognitoUser.signOut()
  }
}
